import React from 'react'
import { dossierHighlightBlockClasses } from '../../../../../utils/style'
import { getClassName, slugify } from '../../../../../utils/utils'
import { useInView } from '../../../../hooks/useInView'
import { InviewOptions } from '../../blocksState'
import { Link } from '../../../../../utils/link'
import { ImageBase } from '../../../../../utils/image'
import { Image } from '../../../../../utils/image'
import { DecorativeIcon } from '../../../../../utils/spaarneIcons'
import i18next from 'i18next'
import { relatedContentSet, relatedContentSrc } from '../../../../../utils/imageSizes'
import { SearchableNewsPage } from '../../../../../backendApi/generated'
import { transformImage } from '../../../sharepointPageApi/transformers/pageDataToPage'

export type DossierHighlightBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  introClassName: string
  contentClassName: string
  gridListClassName: string
  gridItemClassName: string
  gridArticleClassName: string
  gridLinkClassName: string
  gridContentClassName: string
  gridHeaderClassName: string
  gridDateClassName: string
  gridTitleClassName: string
  gridFauxLinkWrapperClassName: string
  gridFauxLinkClassName: string
  gridVisualClassName: string
  gridImageClassName: string
  dossierArticleClassName: string
  dossierLinkClassName: string
  dossierHeaderClassName: string
  dossierContentClassName: string
  dossierTitleClassName: string
  dossierIntroClassName: string
  dossierFauxLinkWrapperClassName: string
  dossierFauxLinkLabelClassName: string
  dossierVisualClassName: string
  dossierImageClassName: string
  dossierLabelClassName: string
}>

type DossierProps = {
  title: string
  intro: string
  url: string
  image: ImageBase
}

type DossierHighlightBlockProps = {
  index: number
  inview?: InviewOptions
  title?: string
  intro?: string
  highlight: DossierProps
  items: NewsItem[]
}


export type NewsItem = Pick<SearchableNewsPage, 'HeaderTitle' | 'AbsolutePath' | 'HeaderImage'>

export const DossierHighlightBlockLayout = (props: DossierHighlightBlockProps) => {
  const { index, inview, intro, items, title } = props
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("DossierHighlightBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  return <section
    key={`block-${index}`}
    id={`io-${index}`}
    ref={ref}
    className={getClassName(dossierHighlightBlockClasses, 'blockClassName', 'container') + ' ' + (inView ? getClassName(dossierHighlightBlockClasses, 'inviewClassName', 'inview') : '')}
  >
    <div className={getClassName(dossierHighlightBlockClasses, 'innerClassName', 'py-5')}>
      <div className="grid">
        <HighlightDossierLayout {...props.highlight} />

        {items.length > 0 && (
          <div className={getClassName(dossierHighlightBlockClasses, 'contentClassName', '')}>
            <ul className={getClassName(dossierHighlightBlockClasses, 'gridListClassName', 'list-unstyled mb-0 row')}>
              {items.map((item) => (
                <RelatedContentItemLayout {...item} key={`rc-${slugify(item.AbsolutePath ?? '#')}`} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  </section>
}

const HighlightDossierLayout = (p: DossierProps) => (
  <article className={getClassName(dossierHighlightBlockClasses, 'dossierArticleClassName', '')}>
    <Link
      url={p.url}
      className={getClassName(dossierHighlightBlockClasses, 'dossierLinkClassName', '')}
    >
      <div className={getClassName(dossierHighlightBlockClasses, 'dossierVisualClassName', '')}>
        <span className={getClassName(dossierHighlightBlockClasses, 'dossierLabelClassName', 'badge')}>
          <DecorativeIcon iconId={"icon--folders"} width="24px" height="24px" />
          <span>{i18next.t("dossier:DossierTag")}</span>
        </span>
        <Image
          {...p.image}
          src={relatedContentSrc} // TODO
          srcset={relatedContentSet} // TODO
          classNames={{ image: getClassName(dossierHighlightBlockClasses, 'dossierImageClassName', '') }}
          width="392"
          height="196"
          lazyload={true}
          sizes="(max-width: 576px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 392px"
        />
      </div>
      <div className={getClassName(dossierHighlightBlockClasses, 'dossierContentClassName', '')}>
        <header className={getClassName(dossierHighlightBlockClasses, 'dossierHeaderClassName', '')}>
          {p.title && (
            <h3 className={getClassName(dossierHighlightBlockClasses, 'dossierTitleClassName', '')}>
              {p.title}
            </h3>
          )}
        </header>

        {p.intro && <p className={getClassName(dossierHighlightBlockClasses, 'dossierIntroClassName', '')} dangerouslySetInnerHTML={{ __html: p.intro }} />}

        <footer aria-hidden="true" className={getClassName(dossierHighlightBlockClasses, 'dossierFauxLinkWrapperClassName', '')}>
          <Link url={p.url} className={getClassName(dossierHighlightBlockClasses, 'dossierFauxLinkLabelClassName', '')}>
            {i18next.t('common:readMore')}
          </Link>
        </footer>
      </div>
    </Link>
  </article>
)

const RelatedContentItemLayout = (p: NewsItem) => {
  const title = p.HeaderTitle ?? "No title"
  const url = p.AbsolutePath ?? "#"
  const image = transformImage({ Title: p.HeaderImage?.Title ?? '', Url: p.HeaderImage?.Url ?? '' })
  return <li className={getClassName(dossierHighlightBlockClasses, 'gridItemClassName', 'col-md-6 col-lg-4 py-3')}>
    <article className={getClassName(dossierHighlightBlockClasses, 'gridArticleClassName', '')}>
      <Link
        url={url}
        className={getClassName(dossierHighlightBlockClasses, 'gridLinkClassName', '')}
      >
        <div className={getClassName(dossierHighlightBlockClasses, 'gridContentClassName', '')}>
          <header className={getClassName(dossierHighlightBlockClasses, 'gridHeaderClassName', '')}>
            {title && (
              <h3 className={getClassName(dossierHighlightBlockClasses, 'gridTitleClassName', '')}>
                {title}
              </h3>
            )}
          </header>

          <footer
            className={getClassName(dossierHighlightBlockClasses, 'gridFauxLinkWrapperClassName', '')}
            aria-hidden="true"
          >
            <div className={getClassName(dossierHighlightBlockClasses, 'gridFauxLinkClassName', '')}>
              <span>
                {i18next.t('common:readMore')}
              </span>
            </div>
          </footer>
        </div>

        <div className={getClassName(dossierHighlightBlockClasses, 'gridVisualClassName', '')}>
          {image && (
            <Image
              {...image}
              src={relatedContentSrc}
              srcset={relatedContentSet}
              classNames={{ image: getClassName(dossierHighlightBlockClasses, 'gridImageClassName', '') }}
              width="392"
              height="196"
              lazyload={true}
              sizes="(max-width: 576px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 392px"
            />
          )}
        </div>
      </Link>
    </article>
  </li>
}
